import { validateCNPJ, validateCPF } from 'validations-br'
import { z } from 'zod'
import { cleanDocument } from '~/helpers/documentNumber'
import { CustomerPFFormSchema, CustomerPJFormSchema } from '../customer/customerForm'
import { EmailFieldSchema } from '../shared/email'
import { required } from '../shared/required'
import { ConfirmPasswordSchema, PASSWORDS_MATCH, PasswordSchema } from './password'

/*
 * Schemas dos formulários de autenticação e cadastro (validação de tela). A regra de senha é a de ./password.ts; os
 * dados PF e PJ do cadastro são os do cliente (schemas/customer/customerForm.ts) com senha e termos. A troca de senha
 * do cliente logado é do domínio customer.
 */

// =========================================================================
// SECTION 1: CAMPOS
// =========================================================================

const TermsSchema = z
    .boolean(required('Você deve concordar com os termos e condições'))
    .refine((value) => value === true, {
        message: 'Você deve concordar com os termos e condições',
    })

// =========================================================================
// SECTION 2: LOGIN E RECUPERAÇÃO
// =========================================================================

export const SignInFormSchema = z.object({
    email: EmailFieldSchema,
    password: z
        .string(required('Campo obrigatório'))
        .min(5, 'Deve conter no mínimo 5 caracteres')
        .max(45, 'Deve conter no máximo 45 caracteres')
        .min(1, 'Deve informar uma senha válida'),
})

/** E-mail sozinho: esqueci minha senha e o login sem conta do carrinho. */
export const EmailFormSchema = z.object({ email: EmailFieldSchema })

/** CPF ou CNPJ (numérico ou alfanumérico) do esqueci minha senha. */
export const DocumentFormSchema = z.object({
    MainDocument: z
        .string(required('Número de documento inválido', 'Deve ser uma string'))
        .nullable()
        .refine((value) => value !== null && value.trim() !== '', {
            message: 'Número de documento inválido',
        })
        .refine(
            (value) => {
                if (!value) return false
                const document = cleanDocument(value)
                if (document.length === 11) return validateCPF(document)
                if (document.length === 14) return validateCNPJ(document)
                return false
            },
            { message: 'Número de documento inválido' },
        ),
})

/** Nova senha pelo link de recuperação. */
export const RecoverPasswordFormSchema = z
    .object({
        newPassword: PasswordSchema,
        newPasswordConfirm: z.string(required('Confirme a nova senha')),
    })
    .refine((data) => data.newPasswordConfirm === data.newPassword, {
        message: 'As senhas não são iguais',
        path: ['newPasswordConfirm'],
    })

// =========================================================================
// SECTION 3: CADASTRO
// =========================================================================

export const PersonTypeFormSchema = z.object({
    personType: z
        .enum(['PF', 'PJ'], required('Deve informar um tipo de pessoa', 'Deve informar um tipo de pessoa válido'))
        .describe('Tipo de pessoa'),
})

/** Cadastro PF: os dados do cliente PF com termos, senha e confirmação. */
export const RegisterPersonFormSchema = CustomerPFFormSchema.extend({
    terms: TermsSchema,
    password: PasswordSchema,
    confirmPassword: ConfirmPasswordSchema,
}).refine((data) => data.confirmPassword === data.password, PASSWORDS_MATCH)

/** Cadastro PJ: os dados do cliente PJ com termos, senha e confirmação. */
export const RegisterCompanyFormSchema = CustomerPJFormSchema.extend({
    terms: TermsSchema,
    password: PasswordSchema,
    confirmPassword: ConfirmPasswordSchema,
}).refine((data) => data.confirmPassword === data.password, PASSWORDS_MATCH)

export type ISignInForm = z.infer<typeof SignInFormSchema>
export type IEmailForm = z.infer<typeof EmailFormSchema>
export type IDocumentForm = z.infer<typeof DocumentFormSchema>
export type IRecoverPasswordForm = z.infer<typeof RecoverPasswordFormSchema>
export type IPersonTypeForm = z.infer<typeof PersonTypeFormSchema>
export type IRegisterPersonForm = z.infer<typeof RegisterPersonFormSchema>
export type IRegisterCompanyForm = z.infer<typeof RegisterCompanyFormSchema>
